// SPDX-License-Identifier: LicenseRef-Proprietary

import type { AbstractTriageBrief } from '../../../lib/models';

/**
 * Enhanced paper analysis status types for clearer UX communication
 * Distinguishes between "analyzed with no results" and "processing failed"
 */
export type PaperAnalysisStatus = 
  | 'not_analyzed'      // Paper hasn't been processed
  | 'analyzing'         // Currently being analyzed
  | 'analyzed_relevant' // Successfully analyzed with relevant results
  | 'analyzed_empty'    // Successfully analyzed but no relevant results found
  | 'analysis_failed'   // Technical failure during analysis
  | 'analysis_error';   // Error with specific details

/**
 * Status information for display purposes
 */
export interface PaperStatusInfo {
  status: PaperAnalysisStatus;
  message?: string;
  errorCode?: string;
  relevanceCount?: number; // Number of relevant parameters found
}

/**
 * Status display configuration for UI rendering
 */
export interface StatusDisplay {
  badge: string;
  color: 'success' | 'neutral' | 'error' | 'processing' | 'default';
  message: string;
  icon?: string;
}

/**
 * Determine paper analysis status based on available data
 */
export function determinePaperStatus(
  brief?: AbstractTriageBrief,
  error?: { code: string; message: string },
  isLoading?: boolean
): PaperAnalysisStatus {
  if (isLoading) {
    return 'analyzing';
  }
  
  if (brief) {
    // Check if the brief contains relevant results
    const hasRelevantResults = (
      (brief.potentialInputSources && brief.potentialInputSources.length > 0 && 
       !brief.potentialInputSources.every(source => source === 'none')) ||
      (brief.retrievable_inputs && brief.retrievable_inputs.some(input => 
        input.likelihood === 'explicit_in_abstract' || 
        input.likelihood === 'likely_in_full_text'
      ))
    );
    
    return hasRelevantResults ? 'analyzed_relevant' : 'analyzed_empty';
  }

  if (error) {
    return 'analysis_failed';
  }
  
  return 'not_analyzed';
}

/**
 * Get display configuration for a paper analysis status
 */
export function getStatusDisplay(
  status: PaperAnalysisStatus, 
  brief?: AbstractTriageBrief, 
  error?: { code: string; message: string }
): StatusDisplay {
  switch (status) {
    case 'analyzed_relevant': {
      const relevanceCount = (brief?.potentialInputSources?.filter(s => s !== 'none').length || 0) +
                            (brief?.retrievable_inputs?.filter(input => 
                              input.likelihood === 'explicit_in_abstract' || 
                              input.likelihood === 'likely_in_full_text'
                            ).length || 0);
      return {
        badge: 'Analyzed',
        color: 'success',
        message: `Found ${relevanceCount} relevant parameter${relevanceCount !== 1 ? 's' : ''}`,
        icon: ''
      };
    }
    
    case 'analyzed_empty':
      return {
        badge: 'Analyzed',
        color: 'neutral',
        message: 'No relevant parameters found',
        icon: ''
      };
    
    case 'analysis_failed':
      return {
        badge: 'Failed',
        color: 'error',
        message: error?.message || 'Analysis failed',
        icon: ''
      };
    
    case 'analysis_error':
      return {
        badge: 'Error',
        color: 'error',
        message: error?.message || 'Processing error occurred',
        icon: ''
      };
    
    case 'analyzing':
      return {
        badge: 'Analyzing...',
        color: 'processing',
        message: 'AI analysis in progress',
        icon: ''
      };
    
    case 'not_analyzed':
    default:
      return {
        badge: 'Not analyzed',
        color: 'default',
        message: 'Select to analyze',
        icon: ''
      };
  }
}

/**
 * Get CSS class name for status color
 */
export function getStatusColorClass(color: StatusDisplay['color']): string {
  switch (color) {
    case 'success':
      return 'evidence-drawer__status--success';
    case 'neutral':
      return 'evidence-drawer__status--neutral';
    case 'error':
      return 'evidence-drawer__status--error';
    case 'processing':
      return 'evidence-drawer__status--processing';
    case 'default':
    default:
      return 'evidence-drawer__status--default';
  }
}
